import React, { useState, useEffect } from 'react';
import { securityApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  ShieldCheck, Warning, Lock, Eye, Users, Clock, Lightning,
  WarningCircle, CheckCircle, XCircle, SignIn, SignOut, Key,
  Detective, Fingerprint,
} from '@phosphor-icons/react';

const ACTION_CONFIG = {
  login: { label: 'Login', icon: SignIn, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  login_failed: { label: 'Failed Login', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  logout: { label: 'Logout', icon: SignOut, color: 'text-slate-500', bg: 'bg-slate-50' },
  password_change: { label: 'Password Changed', icon: Key, color: 'text-amber-600', bg: 'bg-amber-50' },
  password_reset: { label: 'Password Reset', icon: Lock, color: 'text-violet-600', bg: 'bg-violet-50' },
};

const KpiCard = ({ label, value, icon: Icon, color, bgColor, alert }) => (
  <Card className={`${bgColor} border-0 shadow-sm overflow-hidden`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
          <p className={`text-2xl font-bold font-['Outfit'] mt-1 ${color}`}>{value}</p>
        </div>
        <div className={`h-10 w-10 rounded-xl bg-white/80 flex items-center justify-center shadow-sm ${alert ? 'animate-pulse' : ''}`}>
          <Icon className={`h-5 w-5 ${color}`} weight="duotone" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SecurityDashboardPage = () => {
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, sessRes] = await Promise.all([
          securityApi.dashboard(),
          securityApi.activeSessions(),
        ]);
        setData(dashRes.data);
        setSessions(sessRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" />
    </div>
  );

  if (!data) return <div className="text-center py-16 text-slate-500">Failed to load security data.</div>;

  return (
    <div className="space-y-5" data-testid="security-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-900 rounded-2xl p-6 text-white" data-testid="security-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
              <ShieldCheck className="h-6 w-6 text-emerald-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']" data-testid="security-heading">HIPAA Security Dashboard</h1>
              <p className="text-sm text-slate-300 mt-0.5">Access control, audit trails & compliance monitoring</p>
            </div>
          </div>
          <div className="text-right hidden md:block">
            <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs">HIPAA Compliant</Badge>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-1">
        {[
          { key: 'overview', label: 'Overview', icon: Eye },
          { key: 'sessions', label: 'Active Sessions', icon: Users },
          { key: 'events', label: 'Security Events', icon: Clock },
          { key: 'threats', label: 'Threat Detection', icon: Warning },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? 'border-emerald-500 text-emerald-700 bg-emerald-50/50' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ========== OVERVIEW TAB ========== */}
      {tab === 'overview' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Failed Logins (7d)" value={data.failed_logins_week} icon={XCircle}
              color={data.failed_logins_week > 10 ? 'text-red-600' : 'text-amber-600'}
              bgColor={data.failed_logins_week > 10 ? 'bg-red-50' : 'bg-amber-50'}
              alert={data.failed_logins_week > 10} />
            <KpiCard label="Successful Logins (7d)" value={data.successful_logins_week} icon={CheckCircle}
              color="text-emerald-600" bgColor="bg-emerald-50" />
            <KpiCard label="Active Sessions" value={data.active_sessions} icon={Users}
              color="text-sky-600" bgColor="bg-sky-50" />
            <KpiCard label="PHI Accesses (7d)" value={data.phi_accesses_week} icon={Fingerprint}
              color="text-violet-600" bgColor="bg-violet-50" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Failed Today" value={data.failed_logins_today} icon={Warning}
              color={data.failed_logins_today > 5 ? 'text-red-600' : 'text-slate-600'}
              bgColor={data.failed_logins_today > 5 ? 'bg-red-50' : 'bg-slate-50'}
              alert={data.failed_logins_today > 5} />
            <KpiCard label="Password Changes (30d)" value={data.password_changes_month} icon={Key}
              color="text-amber-600" bgColor="bg-amber-50" />
            <KpiCard label="Locked Accounts" value={data.locked_accounts} icon={Lock}
              color={data.locked_accounts > 0 ? 'text-red-600' : 'text-emerald-600'}
              bgColor={data.locked_accounts > 0 ? 'bg-red-50' : 'bg-emerald-50'}
              alert={data.locked_accounts > 0} />
            <KpiCard label="Audit Events (30d)" value={data.total_audit_events_month} icon={Eye}
              color="text-slate-700" bgColor="bg-slate-50" />
          </div>

          {/* HIPAA Compliance Checklist */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-500" /> HIPAA Compliance Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {[
                  { label: 'Role-Based Access Control', done: true },
                  { label: 'Multi-Tenant Data Isolation', done: true },
                  { label: 'Audit Trail Logging', done: true },
                  { label: 'Password Policy (8+ chars, mixed)', done: true },
                  { label: 'Session Timeout (15 min)', done: true },
                  { label: 'Brute Force Protection', done: true },
                  { label: 'PHI Access Logging', done: true },
                  { label: 'Failed Login Tracking', done: true },
                  { label: 'Super Admin PHI Restriction', done: true },
                  { label: 'IP Address Logging', done: true },
                  { label: 'Force Password Change', done: true },
                  { label: 'HTTPS/TLS Encryption', done: true },
                  { label: '2FA Authentication', done: true },
                  { label: 'Device/IP Tracking', done: true },
                  { label: 'Data Export Restrictions', done: true },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${item.done ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500'}`}>
                    {item.done ? <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" weight="fill" /> : <Clock className="h-4 w-4 text-slate-400 shrink-0" />}
                    <span className="font-medium">{item.label}</span>
                    {!item.done && <Badge className="ml-auto bg-slate-200 text-slate-500 border-0 text-[8px]">Phase 2</Badge>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== ACTIVE SESSIONS TAB ========== */}
      {tab === 'sessions' && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Users className="h-4 w-4 text-sky-500" /> Active Sessions ({sessions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">User</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Role</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">IP Address</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Device</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last Activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.length > 0 ? sessions.map((s, i) => (
                  <TableRow key={i} className="hover:bg-sky-50/50">
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{s.user_name}</p>
                        <p className="text-[10px] text-slate-400">{s.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell><Badge className="bg-sky-100 text-sky-700 border-0 text-[10px] capitalize">{s.role?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-xs font-mono text-slate-600">{s.ip_address}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{s.user_agent}</TableCell>
                    <TableCell className="text-xs text-slate-500">{s.last_activity ? new Date(s.last_activity).toLocaleString('en-IN') : '-'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No active sessions</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ========== SECURITY EVENTS TAB ========== */}
      {tab === 'events' && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" /> Recent Security Events
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {(data.recent_events || []).map((e, i) => {
                const config = ACTION_CONFIG[e.action] || { label: e.action, icon: Eye, color: 'text-slate-500', bg: 'bg-slate-50' };
                const Icon = config.icon;
                return (
                  <div key={i} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors">
                    <div className={`h-8 w-8 rounded-lg ${config.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`h-4 w-4 ${config.color}`} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={`${config.bg} ${config.color} border-0 text-[10px]`}>{config.label}</Badge>
                        <span className="text-xs text-slate-500 truncate">{e.user}</span>
                      </div>
                      {e.details && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{e.details}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-slate-400 font-mono">{e.ip}</p>
                      <p className="text-[10px] text-slate-400">{e.time ? new Date(e.time).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : ''}</p>
                    </div>
                  </div>
                );
              })}
              {(!data.recent_events || data.recent_events.length === 0) && (
                <div className="text-center py-12 text-slate-400">
                  <Clock className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No security events recorded yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========== THREAT DETECTION TAB ========== */}
      {tab === 'threats' && (
        <div className="space-y-5">
          {/* Suspicious IPs */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Detective className="h-4 w-4 text-red-500" /> Suspicious IP Addresses (3+ failed attempts in 7 days)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-red-50/50">
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">IP Address</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Failed Attempts</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last Attempt</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Risk</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.suspicious_ips || []).length > 0 ? data.suspicious_ips.map((s, i) => (
                    <TableRow key={i} className="hover:bg-red-50/30">
                      <TableCell className="text-sm font-mono text-slate-700">{s.ip}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${s.attempts >= 10 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} border-0 text-xs`}>{s.attempts}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{new Date(s.last).toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${s.attempts >= 10 ? 'bg-red-600 text-white' : s.attempts >= 5 ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-700'} border-0 text-[10px]`}>
                          {s.attempts >= 10 ? 'HIGH' : s.attempts >= 5 ? 'MEDIUM' : 'LOW'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8">
                        <CheckCircle className="h-10 w-10 mx-auto mb-2 text-emerald-300" />
                        <p className="text-sm text-emerald-600 font-medium">No suspicious activity detected</p>
                        <p className="text-xs text-slate-400 mt-1">All clear in the last 7 days</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Locked Accounts */}
          <Card className={`border shadow-sm ${data.locked_accounts > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <Lock className={`h-6 w-6 ${data.locked_accounts > 0 ? 'text-red-600' : 'text-emerald-600'}`} weight="duotone" />
              <div>
                <p className={`text-sm font-semibold ${data.locked_accounts > 0 ? 'text-red-800' : 'text-emerald-800'}`}>
                  {data.locked_accounts > 0 ? `${data.locked_accounts} Account(s) Locked` : 'No Locked Accounts'}
                </p>
                <p className={`text-xs ${data.locked_accounts > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {data.locked_accounts > 0 ? 'Accounts locked due to 5+ consecutive failed login attempts. Auto-unlocks after 15 minutes.' : 'All accounts are accessible.'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SecurityDashboardPage;
